import { useEffect, useState } from "react";
import { BigButton } from "../components/BigButton";
import { speak, stopSpeaking } from "../speech";

/**
 * What the three tabs mean, shown once, before a child's first question.
 *
 * Cards rather than coach marks pointing at the real buttons. Coach marks ask a
 * six-year-old to read small text beside a highlighted thing and understand
 * that an arrow means "over there" — and they break the moment a layout moves.
 * One idea per card, at the size everything else in this app is, is a register
 * a five-year-old already knows from picture books.
 *
 * Three cards and no more. This is the door, not the manual: everything here is
 * discoverable by tapping, and the guide only has to make a child unafraid to
 * tap. The real teaching is the first star.
 */

interface Card {
  emoji: string;
  title: string;
  body: string;
  /** Read aloud verbatim, so it has to sound right spoken, not just look right. */
  spoken: string;
}

const CARDS: Card[] = [
  {
    emoji: "💬",
    title: "Ask me anything",
    body: "Type it or hold the microphone and say it. I'll answer, then ask you one question back to see what you think.",
    spoken:
      "Ask me anything. Type it, or hold the microphone and say it out loud. I will answer, and then ask you one question back.",
  },
  {
    emoji: "🌟",
    title: "My sky",
    body: "Every question you finish becomes a star. Stars get sleepy if you leave them a while — come back and wake them up.",
    spoken:
      "Every question you finish becomes a star in your sky. Stars get sleepy if you leave them for a while. Come back and wake them up.",
  },
  {
    emoji: "🎁",
    title: "My goal",
    body: "Stars earn you stardust. Save it up for something you want, and a grown-up says yes.",
    spoken:
      "Stars earn you stardust. Save it up for something you want, and a grown up says yes.",
  },
];

export function GuideScreen({ autoRead, onDone }: { autoRead: boolean; onDone: () => void }) {
  const [index, setIndex] = useState(0);
  const card = CARDS[index]!;
  const last = index === CARDS.length - 1;

  /*
    Read each card aloud to the youngest band without being asked. A guide a
    five-year-old cannot read is not a guide, and this is the one screen where
    they have not yet learned that the speaker button exists.
  */
  useEffect(() => {
    if (!autoRead) return;
    speak(card.spoken);
    return () => stopSpeaking();
  }, [index, autoRead, card.spoken]);

  useEffect(() => () => stopSpeaking(), []);

  const next = () => {
    stopSpeaking();
    if (last) onDone();
    else setIndex((i) => i + 1);
  };

  return (
    <div className="flex h-dvh flex-col items-center justify-center px-6">
      <div key={index} className="rise w-full max-w-md text-center">
        <div className="text-[76px] leading-none">{card.emoji}</div>
        <h1 className="disp mt-5 text-[34px] leading-tight text-cream">{card.title}</h1>
        <p className="mx-auto mt-3 max-w-[24rem] text-[19px] leading-relaxed text-cream/75">
          {card.body}
        </p>

        <div className="mt-9">
          <BigButton onClick={next}>{last ? "Let's go" : "Next"}</BigButton>
        </div>

        <button
          onClick={() => speak(card.spoken)}
          className="kidbtn disp mt-4 rounded-full px-5 py-2 text-[16px] text-cream/60"
        >
          🔊 Read it to me
        </button>
      </div>

      {/* Where they are, without numbers a five-year-old has to parse. */}
      <div className="mt-10 flex items-center gap-2" aria-hidden>
        {CARDS.map((c, i) => (
          <span
            key={c.title}
            className={`h-[8px] rounded-full transition-all duration-300 ${
              i === index ? "w-7 bg-gold" : "w-[8px] bg-cream/25"
            }`}
          />
        ))}
      </div>

      {/*
        Deliberately quiet, and deliberately present. A child who has been shown
        this before — a second tablet, a parent replaying it — should not have
        to sit through it, but nothing about skipping should look like the way
        forward for the child seeing it the first time.
      */}
      <button onClick={onDone} className="kidbtn mt-6 text-[15px] text-cream/35">
        Skip
      </button>
    </div>
  );
}
